/**
 * SRES · Professional Section Engine (Sprint P27 · P1)
 * ═════════════════════════════════════════════════════
 *
 * Named section sets with:
 *   · N-S / E-W automatic orientation
 *   · Arbitrary azimuth
 *   · Corridor width (thickness) + spacing + n-sections
 *   · Draft → Review → Approved → Locked workflow
 *   · PDF + DOCX appendix export
 *   · Duplicate / archive / delete
 *
 * Backend endpoints:
 *   GET    /api/projects/{pid}/section-sets
 *   POST   /api/projects/{pid}/section-sets
 *   PATCH  /api/projects/{pid}/section-sets/{sid}
 *   DELETE /api/projects/{pid}/section-sets/{sid}
 *   POST   /api/projects/{pid}/section-sets/{sid}/approve
 *   POST   /api/projects/{pid}/section-sets/{sid}/lock
 *   GET    /api/projects/{pid}/section-sets/{sid}/appendix.pdf
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Waveform, Play, TrashSimple, Copy, Signature, Lock,
  LockOpen, DownloadSimple, FilePdf, FileDoc, Warning,
  CheckCircle, ArrowClockwise,
} from "@phosphor-icons/react";
import {
  listSectionSets, createSectionSet, patchSectionSet, deleteSectionSet,
  duplicateSectionSet, approveSectionSet, rejectSectionSet, lockSectionSet,
  sectionSetAppendixUrl, sectionSetAppendixDocxUrl,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";


const ORIENTATIONS = [
  { key: "ew",   label: "E-W (strike ~E-W)",  azimuth: 90 },
  { key: "ns",   label: "N-S (strike ~N-S)",  azimuth: 0 },
  { key: "azim", label: "Custom azimuth",     azimuth: 45 },
];


function StatusPill({ status }) {
  const tone = {
    draft:      "bg-slate-200 text-slate-700",
    review:     "bg-sky-200 text-sky-800",
    approved:   "bg-emerald-200 text-emerald-800",
    rejected:   "bg-rose-200 text-rose-800",
    locked:     "bg-amber-200 text-amber-800",
    archived:   "bg-slate-100 text-slate-500",
    superseded: "bg-slate-100 text-slate-500",
  }[status] || "bg-slate-100 text-slate-500";
  return (
    <Badge className={`${tone} text-[9px] font-mono uppercase tracking-widest`}>
      {status}
    </Badge>
  );
}


// Safely extract an error message from a FastAPI/axios error payload.
function errMsg(err, fallback = "Request failed") {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (typeof data?.detail === "string") return data.detail;
  if (Array.isArray(data?.detail)) {
    return data.detail.map((d) => d?.msg || JSON.stringify(d)).join(" · ");
  }
  return err?.message || fallback;
}


function SectionRow({ pid, set, onDeleted }) {
  const qc = useQueryClient();
  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("sres_token") || "";
  const pdfUrl = `${backend}/api${sectionSetAppendixUrl(pid, set.id).replace(/^\/api/, "")}?_auth=${encodeURIComponent(token)}`;
  const docxUrl = `${backend}/api${sectionSetAppendixDocxUrl(pid, set.id).replace(/^\/api/, "")}?_auth=${encodeURIComponent(token)}`;

  const approveMut = useMutation({
    mutationFn: () => approveSectionSet(pid, set.id,
      { action: "approved", note: "CP sign-off" }),
    onSuccess: () => { toast.success(`Approved · ${set.name}`); qc.invalidateQueries({ queryKey: ["section-sets", pid] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const lockMut = useMutation({
    mutationFn: () => lockSectionSet(pid, set.id,
      { action: "locked", note: "Locked" }),
    onSuccess: () => { toast.success(`Locked · ${set.name}`); qc.invalidateQueries({ queryKey: ["section-sets", pid] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const patchMut = useMutation({
    mutationFn: (patch) => patchSectionSet(pid, set.id, patch),
    onSuccess: () => { toast.success("Updated"); qc.invalidateQueries({ queryKey: ["section-sets", pid] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const rejectMut = useMutation({
    mutationFn: () => rejectSectionSet(pid, set.id,
      { action: "rejected", note: "Rejected" }),
    onSuccess: () => { toast.success("Rejected · returned to draft"); qc.invalidateQueries({ queryKey: ["section-sets", pid] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const dupMut = useMutation({
    mutationFn: () => duplicateSectionSet(pid, set.id),
    onSuccess: () => { toast.success("Duplicated"); qc.invalidateQueries({ queryKey: ["section-sets", pid] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delMut = useMutation({
    mutationFn: () => deleteSectionSet(pid, set.id),
    onSuccess: () => { toast.success("Deleted"); onDeleted?.(); qc.invalidateQueries({ queryKey: ["section-sets", pid] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const isLocked = set.status === "locked" || set.locked;

  return (
    <div className="border border-slate-200 bg-white rounded p-3 space-y-2"
         data-testid={`section-set-row-${set.id}`}>
      <div className="flex items-center gap-2">
        <Waveform size={13} className="text-brand-blue" weight="duotone" />
        <div className="text-[13px] font-semibold text-slate-800 truncate">{set.name}</div>
        <StatusPill status={set.status || "draft"} />
        <div className="ml-auto text-[10px] font-mono text-slate-500">
          {set.n_sections || set.max_sections || "?"} sections · {set.orientation || "ew"} ·
          spacing {set.spacing || "?"} m
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[10px] font-mono text-slate-500">
        <div><span className="text-slate-400">Type · </span>{set.type || "cross"}</div>
        <div><span className="text-slate-400">Az · </span>{set.azimuth ?? "—"}°</div>
        <div><span className="text-slate-400">Thickness · </span>{set.thickness || "—"} m</div>
        <div><span className="text-slate-400">V× · </span>{set.v_exag || 1}</div>
        <div><span className="text-slate-400">Holes · </span>{(set.included_holes || []).length || "auto"}</div>
        <div><span className="text-slate-400">Updated · </span>{set.updated_at ? new Date(set.updated_at).toLocaleDateString() : "—"}</div>
      </div>
      <div className="flex flex-wrap items-center gap-1 pt-1 border-t border-slate-100">
        {set.status === "draft" && (
          <Button size="sm" variant="outline" className="h-7 text-[10px]"
                  onClick={() => patchMut.mutate({ status: "review" })}
                  data-testid={`section-set-review-${set.id}`}>
            → Review
          </Button>
        )}
        {set.status === "review" && (
          <>
            <Button size="sm" variant="outline"
                    className="h-7 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => approveMut.mutate()}
                    disabled={approveMut.isPending}
                    data-testid={`section-set-approve-${set.id}`}>
              <Signature size={11} className="mr-1" /> Approve
            </Button>
            <Button size="sm" variant="outline"
                    className="h-7 text-[10px] border-rose-300 text-rose-700 hover:bg-rose-50"
                    onClick={() => rejectMut.mutate()}
                    disabled={rejectMut.isPending}
                    data-testid={`section-set-reject-${set.id}`}>
              Reject
            </Button>
          </>
        )}
        {set.status === "approved" && !isLocked && (
          <Button size="sm" variant="outline"
                  className="h-7 text-[10px] border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => lockMut.mutate()}
                  data-testid={`section-set-lock-${set.id}`}>
            <Lock size={11} className="mr-1" /> Lock for CPR
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-7 text-[10px]"
                onClick={() => dupMut.mutate()}
                data-testid={`section-set-dup-${set.id}`}>
          <Copy size={11} className="mr-1" /> Duplicate
        </Button>
        <a href={pdfUrl}
           target="_blank" rel="noreferrer"
           className="h-7 px-2 flex items-center gap-1 text-[10px] rounded border border-sky-300 text-sky-700 hover:bg-sky-50"
           data-testid={`section-set-pdf-${set.id}`}>
          <FilePdf size={11} /> PDF
        </a>
        <a href={docxUrl}
           target="_blank" rel="noreferrer"
           className="h-7 px-2 flex items-center gap-1 text-[10px] rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
           data-testid={`section-set-docx-${set.id}`}>
          <FileDoc size={11} /> DOCX
        </a>
        <div className="flex-1" />
        <Button size="sm" variant="outline"
                className="h-7 text-[10px] text-rose-600 border-rose-200"
                onClick={() => window.confirm("Delete this section set?") && delMut.mutate()}
                disabled={isLocked}
                title={isLocked ? "Locked — unlock first" : "Delete"}
                data-testid={`section-set-delete-${set.id}`}>
          <TrashSimple size={11} className="mr-1" /> Delete
        </Button>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// New Section Set form
// ══════════════════════════════════════════════════════════════════════
function NewSetForm({ pid, onCreated }) {
  const [name, setName]        = useState("");
  const [orient, setOrient]    = useState("ew");
  const [azimuth, setAzimuth]  = useState(90);
  const [spacing, setSpacing]  = useState(50);
  const [thickness, setThick]  = useState(25);
  const [maxSections, setMax]  = useState(20);
  const [vExag, setVExag]      = useState(1);

  const createMut = useMutation({
    mutationFn: () => createSectionSet(pid, {
      name: name.trim() || `Section set ${new Date().toISOString().slice(0, 10)}`,
      type: "cross",
      orientation: orient,
      azimuth: orient === "azim" ? Number(azimuth) : (orient === "ns" ? 0 : 90),
      spacing: Number(spacing),
      thickness: Number(thickness),
      max_sections: Number(maxSections),
      v_exag: Number(vExag),
      status: "draft",
    }),
    onSuccess: () => {
      toast.success("Section set created");
      setName("");
      onCreated?.();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2">
      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
        New section set
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Input placeholder="Name (e.g. E-W 50 m)"
               value={name} onChange={(e) => setName(e.target.value)}
               className="h-8 text-[11px] col-span-2"
               data-testid="section-set-new-name" />
        <div>
          <label className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
            Orientation
          </label>
          <select value={orient}
                   onChange={(e) => {
                     setOrient(e.target.value);
                     const o = ORIENTATIONS.find((x) => x.key === e.target.value);
                     if (o && o.key !== "azim") setAzimuth(o.azimuth);
                   }}
                   className="w-full h-8 text-[11px] border border-slate-300 rounded px-1"
                   data-testid="section-set-new-orientation">
            {ORIENTATIONS.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
            Azimuth (°)
          </label>
          <Input type="number" min={0} max={360} step={1}
                 value={azimuth} onChange={(e) => setAzimuth(Number(e.target.value))}
                 disabled={orient !== "azim"}
                 className="h-8 text-[11px]"
                 data-testid="section-set-new-az" />
        </div>
        <div>
          <label className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
            Spacing (m)
          </label>
          <Input type="number" min={5} max={2000} step={5}
                 value={spacing} onChange={(e) => setSpacing(Number(e.target.value))}
                 className="h-8 text-[11px]"
                 data-testid="section-set-new-spacing" />
        </div>
        <div>
          <label className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
            Thickness (m)
          </label>
          <Input type="number" min={1} max={500} step={1}
                 value={thickness} onChange={(e) => setThick(Number(e.target.value))}
                 className="h-8 text-[11px]"
                 data-testid="section-set-new-thickness" />
        </div>
        <div>
          <label className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
            Max sections
          </label>
          <Input type="number" min={1} max={100} step={1}
                 value={maxSections} onChange={(e) => setMax(Number(e.target.value))}
                 className="h-8 text-[11px]"
                 data-testid="section-set-new-max" />
        </div>
        <div>
          <label className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
            Vertical exag (×)
          </label>
          <Input type="number" min={0.5} max={20} step={0.5}
                 value={vExag} onChange={(e) => setVExag(Number(e.target.value))}
                 className="h-8 text-[11px]"
                 data-testid="section-set-new-vexag" />
        </div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Button size="sm"
                onClick={() => createMut.mutate()}
                disabled={createMut.isPending}
                className="h-8 text-[11px] bg-brand-blue hover:bg-brand-blue/90 text-white"
                data-testid="section-set-new-submit">
          {createMut.isPending ? "Creating…" : <><Play size={12} className="mr-1" /> Create section set</>}
        </Button>
        <div className="text-[10px] text-slate-500">
          Corridor width = 2 × thickness. Sections auto-populate from the block
          model bbox intersected with drillhole traces.
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// Root panel
// ══════════════════════════════════════════════════════════════════════
export default function SectionEnginePanel({ projectId }) {
  const q = useQuery({
    queryKey: ["section-sets", projectId],
    queryFn: () => listSectionSets(projectId),
    enabled: !!projectId,
    refetchInterval: 30_000,
  });
  const sets = q.data?.items || q.data?.sets || (Array.isArray(q.data) ? q.data : []);

  const approved = sets.filter((s) => s.status === "approved" || s.status === "locked").length;
  const draft    = sets.filter((s) => s.status === "draft").length;
  const review   = sets.filter((s) => s.status === "review").length;

  return (
    <div className="border border-slate-200 rounded-lg bg-white shadow-sm"
         data-testid="section-engine-panel">
      <div className="bg-slate-900 text-slate-100 px-4 py-3 border-b border-slate-800 flex items-center gap-2 rounded-t-lg">
        <Waveform size={16} className="text-sky-400" weight="duotone" />
        <h3 className="text-[14px] font-bold uppercase tracking-widest">
          Section Engine
        </h3>
        <Badge className="bg-sky-500 text-white text-[9px] font-mono">
          E-W · N-S · AZIMUTH
        </Badge>
        <div className="ml-auto flex items-center gap-2 text-[10px] font-mono text-slate-400">
          <span>{sets.length} sets</span>
          <span className="text-emerald-400">{approved} approved</span>
          <span className="text-sky-400">{review} in review</span>
          <span className="text-slate-500">{draft} drafts</span>
          <button onClick={() => q.refetch()} title="Refresh"
                  className="ml-1 hover:text-white"
                  data-testid="section-engine-refresh">
            <ArrowClockwise size={12} />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <NewSetForm pid={projectId} onCreated={() => q.refetch()} />

        {q.isLoading && (
          <div className="text-[11px] text-slate-500 py-4 text-center animate-pulse">
            Loading section sets…
          </div>
        )}

        {!q.isLoading && sets.length === 0 && (
          <div className="border border-dashed border-slate-300 rounded p-8 text-center">
            <Waveform size={36} className="mx-auto text-slate-300" weight="duotone" />
            <div className="text-[13px] font-bold text-slate-500 mt-2">
              NO SECTION SETS
            </div>
            <div className="text-[10px] text-slate-400 mt-1 max-w-md mx-auto">
              Use the form above to build the first named section set from
              real drillhole traces and block model data.
            </div>
          </div>
        )}

        <div className="space-y-2">
          {sets.map((s) => (
            <SectionRow key={s.id} pid={projectId} set={s}
                         onDeleted={() => q.refetch()} />
          ))}
        </div>
      </div>
    </div>
  );
}
